/**
 * LinkifiedText — renders a plain string as <Text>, turning any http(s) URL it
 * contains into a tappable link (opens via Linking.openURL). Used by the chat
 * bubbles so invite / support links a nurse receives are actually clickable
 * instead of inert text she has to copy by hand.
 */

import type { ReactNode } from "react";
import type { StyleProp, TextStyle } from "react-native";
import { Linking } from "react-native";
import { Text } from "react-native-paper";

import { splitTextWithUrls, stripTrailingPunctuation } from "@/lib/linkify";
import { colors } from "@/lib/theme";

export interface LinkifiedTextProps {
    children: string;
    style?: StyleProp<TextStyle>;
    /** Color for the tappable link segments. Defaults to the brand teal. */
    linkColor?: string;
}

export function LinkifiedText({ children, style, linkColor }: LinkifiedTextProps) {
    const parts = splitTextWithUrls(children);
    const link = linkColor ?? colors.teal600;

    return (
        <Text style={style}>
            {parts.map((part, i): ReactNode => {
                if (!part.isUrl) {
                    return part.text;
                }
                const { url, trailing } = stripTrailingPunctuation(part.text);
                return (
                    <Text key={i}>
                        <Text
                            style={{ color: link, textDecorationLine: "underline" }}
                            onPress={() => {
                                Linking.openURL(url).catch(() => {});
                            }}
                        >
                            {url}
                        </Text>
                        {trailing}
                    </Text>
                );
            })}
        </Text>
    );
}
